import React, {useRef} from "react";
import {ApiError, IWishlistData, Wishlist, WishlistFactory} from "@connox/typescript-library";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import {getShopId} from "../../lib/getShopId";
import window from "../../lib/WindowExtended";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import * as Sentry from "@sentry/browser";
import {Exponea} from "../../../tracking/exponea";
import {PossibleNetworkError} from "../../cart/ShoppingCart/ShoppingCart";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";
import {Tracking} from "../../../tracking/tracking";

type WishlistProviderStateBasicType = {
  wishlist: Wishlist | null;
  isNotLoggedInModalShown: boolean;
  isWishlistFullModalShown: boolean;
  itemIdToBeHandled: number | undefined;
  itemIdAddedByUrl: number | undefined;
};

type WishlistProviderStateType = {isLoading: boolean} & WishlistProviderStateBasicType;

type WishlistContextType = {
  addItem: (itemId: number) => Promise<void>;
  deleteItem: (itemId: number) => Promise<void>;
  addOrDeleteItem: (itemId: number) => Promise<void>;
  setIsNotLoggedInModalShown: (showModal: boolean) => void;
  setIsWishlistFullModalShown: (showModal: boolean) => void;
  setItemIdAddedByUrl: (itemId: number | undefined) => void;
} & WishlistProviderStateBasicType;

const wishlistContext = React.createContext<WishlistContextType | null>(null);

export const useWishlistContext = (): WishlistContextType => {
  const context = React.useContext(wishlistContext);
  if (!context) {
    throw new Error("useWishlistContext must be used in a component within a WishlistContextProvider.");
  }

  return context;
};

const initialWishlistState: WishlistProviderStateType = {
  wishlist: null,
  isNotLoggedInModalShown: false,
  isWishlistFullModalShown: false,
  itemIdToBeHandled: undefined,
  isLoading: true,
  itemIdAddedByUrl: undefined,
};

const WishlistContextProvider: React.FC = ({children}) => {
  /**
   * handle WISHLIST_MAX_ITEMS_REACHED error as normal behaviour
   * other kinds of errors will be pumped to sentry
   * @param error
   * @param itemId
   * @param call
   */
  const errorHandling = (error, itemId: number, call: "add" | "delete") => {
    const tempError = ((error as ApiError).originalError ?? error) as PossibleNetworkError;
    const isMaxItemsReached = !!tempError?.response?.data.messages?.some((msg) => msg.code === MAX_ITEMS_REACHED);
    if (isMaxItemsReached) {
      setIsWishlistFullModalShown(true);
    } else {
      tempError.name = "Wishlist" + tempError.name;
      Sentry.captureException(tempError, {
        extra: {
          occurence: `PDP wishlist ${call} (Item: ${String(itemId)}, wlId: ${
            state.wishlist ? state.wishlist.id : `no_wishlist_on_${call}`
          })`,
        },
      });
    }
  };

  /**
   * add or delete item, dependent on if its already into the wl or not
   * @param itemId
   */
  const addOrDeleteItem = async (itemId: number) => {
    if (!customer.isLoggedIn) {
      setItemIdToBeHandled(itemId);
      setIsNotLoggedInModalShown(true);

      return;
    }

    if (!state.wishlist) {
      setItemIdToBeHandled(itemId);
      await retrieveWishlist();

      return;
    }

    const isAlreadyAdded = state.wishlist.items.some(({item}) => item.itemId === itemId);
    if (!isAlreadyAdded) {
      await addItem(itemId);
    } else {
      await deleteItem(itemId);
    }
  };

  /**
   * add item to wishlist
   * @param itemId
   */
  const addItem = async (itemId: number) => {
    if (!state.wishlist) {
      return;
    }

    return state.wishlist
      .addItem(itemId)
      .then(() => {
        if (!state.wishlist) return;

        const {items} = state.wishlist;
        // get the item of interest after it is added, for exponea
        const addedItem = items.find((itm) => itm.item.itemId === itemId);
        if (addedItem) {
          Tracking.sendAddToWishlist(items, addedItem);
        }
      })
      .catch((error) => {
        errorHandling(error, itemId, "add");
      });
  };

  /**
   * delete item from wishlist
   * @param itemId
   */
  const deleteItem = async (itemId: number) => {
    if (!state.wishlist) return;

    // get the item of interest BEFORE it is deleted, for exponea
    const itemToDelete = state.wishlist.items.find((itm) => itm.item.itemId === itemId);

    return state.wishlist
      .deleteItem(itemId)
      .then(() => {
        if (!state.wishlist || !itemToDelete) return;
        Tracking.sendRemoveFromWishlist(state.wishlist.items, itemToDelete);
      })
      .catch((error) => {
        errorHandling(error, itemId, "delete");
      });
  };

  const setIsNotLoggedInModalShown = (param: boolean) =>
    setState((prevState: WishlistProviderStateType) => ({
      ...prevState,
      isNotLoggedInModalShown: param,
      isLoading: false,
    }));

  const setIsWishlistFullModalShown = (param: boolean) =>
    setState((prevState) => ({...prevState, isWishlistFullModalShown: param}));

  const setItemIdToBeHandled = (itemId?: number) =>
    setState((prevState) => ({...prevState, itemIdToBeHandled: itemId}));

  const setItemIdAddedByUrl = (itemId?: number) => setState((prevState) => ({...prevState, itemIdAddedByUrl: itemId}));

  const [state, setState] = React.useState<WishlistProviderStateType>(initialWishlistState);
  const wishlistRef = useRef<Wishlist | null>(null);
  const {customer} = useCustomerContext();
  const storage = new BrowserStorage<IWishlistData | null>("sessionStorage", "wishlist");

  const setWishlistState = (wishlist: Wishlist | null) =>
    setState({
      ...initialWishlistState,
      wishlist,
      isLoading: false,
    });

  const updateWishlist = () => {
    storage.write(
      customer.visitorId,
      wishlistRef.current
        ? {id: wishlistRef.current.id, items: wishlistRef.current.items, modified: wishlistRef.current.modified}
        : null,
    );
    setWishlistState(wishlistRef.current);
  };

  const initWishlist = (wishlist: Wishlist | null) => {
    wishlistRef.current = wishlist;
    if (wishlist) {
      wishlist.emitter.on(updateWishlist);
      storage.write(customer.visitorId, {id: wishlist.id, items: wishlist.items, modified: wishlist.modified});
    }
    setWishlistState(wishlist);
  };

  const initWishListFromStorageData = (data: IWishlistData) => {
    const wishlist = new Wishlist(getShopId(), window.c_ActiveCurrency, customer.visitorId, data);
    wishlist.emitter.on(updateWishlist);
    wishlistRef.current = wishlist;
    setWishlistState(wishlist);
  };

  const retrieveWishlist = async () => {
    const urlItemId = UrlSearchParamsHelper.wishlistItemId;
    if (customer.isLoggedIn) {
      if (state.itemIdToBeHandled || urlItemId) {
        try {
          const tempWishlist = await WishlistFactory.addItemToNewWishlist(
            getShopId(),
            window.c_ActiveCurrency,
            customer.visitorId,
            state.itemIdToBeHandled || urlItemId || 0,
          );
          const addedItem = tempWishlist.items.find(
            (itm) => itm.item.itemId === (state.itemIdToBeHandled || urlItemId),
          );
          if (addedItem) {
            Tracking.sendAddToWishlist(tempWishlist.items, addedItem);
          }
          if (state.itemIdToBeHandled) setItemIdToBeHandled(undefined);
          initWishlist(tempWishlist);
          if (urlItemId) setItemIdAddedByUrl(urlItemId);
        } catch (error) {
          errorHandling(error, state.itemIdToBeHandled ?? 0, "add");
        }
      } else {
        const storedDataOutsideWishlistPage = document.querySelector<HTMLDivElement>(".wishlist")
          ? null
          : storage.read(customer.visitorId);
        // if wishlist class does not exist get data from session storage
        if (storedDataOutsideWishlistPage) {
          initWishListFromStorageData(storedDataOutsideWishlistPage);
        } else {
          // if wishlist class exist get data from api
          const tempWishlist = await WishlistFactory.getWishlist(
            getShopId(),
            window.c_ActiveCurrency,
            customer.visitorId,
          );
          initWishlist(tempWishlist);
        }
      }
    } else {
      if (urlItemId) {
        setItemIdToBeHandled(urlItemId);
        setItemIdAddedByUrl(urlItemId);
        setIsNotLoggedInModalShown(true);
      } else {
        initWishlist(null);
      }
    }
  };

  React.useEffect(() => {
    void retrieveWishlist();
  }, [customer.isLoggedIn]);

  const {Provider} = wishlistContext;

  if (state.isLoading) {
    return null;
  }

  return (
    <Provider
      value={{
        ...state,
        addItem,
        deleteItem,
        addOrDeleteItem,
        setIsNotLoggedInModalShown,
        setIsWishlistFullModalShown,
        setItemIdAddedByUrl,
      }}
    >
      {children}
    </Provider>
  );
};

export default WishlistContextProvider;

const MAX_ITEMS_REACHED = "WISHLIST_MAX_ITEMS_REACHED";
